import React, { useState, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, ClipboardList, RefreshCw, FileDown, FileText, AlertTriangle } from 'lucide-react';
import scheduleService from '../../services/scheduleService';
import excelExportService from '../../services/excelExportService';

const LEVEL_LABELS = { high: '高', medium: '中', low: '低' };
const LEVEL_BADGE_CLASS = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-200 text-gray-600',
};

function RiskBadge({ level }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${LEVEL_BADGE_CLASS[level] || LEVEL_BADGE_CLASS.low}`}>
      {LEVEL_LABELS[level] || level}
    </span>
  );
}

function WorkOrderSummaryDialog({ open, onClose, dateRange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Depend on the primitive date strings, not the dateRange object: the parent
  // passes an inline object (new reference every render, e.g. on window resize),
  // which would otherwise retrigger the auto-run effect on every parent render.
  const rangeStart = dateRange?.start;
  const rangeEnd = dateRange?.end;

  const runSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    setData(null);

    try {
      const result = await scheduleService.getWorkOrderSummary({
        start_date: rangeStart,
        end_date: rangeEnd,
        include_ai: true,
      });

      if (!result?.success) {
        throw new Error(result?.error || '生成工单总结失败');
      }

      setData(result);
    } catch (err) {
      console.error('Work-order summary failed:', err);
      setError(err?.response?.data?.error || err?.message || '生成工单总结失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd]);

  // Auto-run when opened
  useEffect(() => {
    if (open) {
      runSummary();
    }
  }, [open, runSummary]);

  const handleExport = useCallback(async (format) => {
    if (!data) return;
    setExporting(true);
    try {
      const payload = {
        start_date: dateRange?.start,
        end_date: dateRange?.end,
        ai_summary: data.ai_summary || '',
        model: data.model || '',
      };
      const { blob, filename } = format === 'pdf'
        ? await scheduleService.exportWorkOrderSummaryPdf(payload)
        : await scheduleService.exportWorkOrderSummaryHtml(payload);
      excelExportService.downloadBlob(blob, filename);
    } catch (err) {
      console.error(`Export ${format} failed:`, err);
      setError(err?.response?.data?.error || err?.message || '导出失败，请稍后重试');
    } finally {
      setExporting(false);
    }
  }, [data, dateRange]);

  const stats = data?.stats;
  const riskGroups = data?.risk_groups || [];
  const dependencyRows = data?.dependency_rows || [];
  const orders = data?.orders || [];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            工单状态总结
          </DialogTitle>
          <DialogDescription>
            基于每日看板排产与工单进度的风险预警（规则引擎计算）+ AI 状态总结（Qwen）。
            范围：{dateRange?.start || ''} 至 {dateRange?.end || ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 px-6 py-2">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mb-3" />
              <p>正在汇总工单进度并计算风险，AI 总结可能需要数十秒…</p>
            </div>
          )}

          {error && !loading && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!loading && data && (
            <ScrollArea className="h-[55vh] rounded-md border bg-muted/40 p-4">
              {/* 数据概览 */}
              {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  <div className="rounded-md border bg-background p-3">
                    <div className="text-xs text-muted-foreground">活跃工单</div>
                    <div className="text-xl font-bold">{stats.active_orders}</div>
                  </div>
                  <div className="rounded-md border bg-background p-3">
                    <div className="text-xs text-muted-foreground">看板任务(本期)</div>
                    <div className="text-xl font-bold">{stats.board_tasks}</div>
                  </div>
                  <div className="rounded-md border bg-background p-3">
                    <div className="text-xs text-muted-foreground">总完成率</div>
                    <div className="text-xl font-bold">{stats.overall_progress}%</div>
                  </div>
                  <div className="rounded-md border bg-background p-3">
                    <div className="text-xs text-muted-foreground">逾期工单</div>
                    <div className={`text-xl font-bold ${stats.overdue_orders > 0 ? 'text-red-600' : ''}`}>
                      {stats.overdue_orders}
                    </div>
                  </div>
                </div>
              )}

              {/* 工单状态明细 */}
              <h3 className="text-sm font-semibold mb-2">一、工单状态明细</h3>
              {orders.length > 0 ? (
                <table className="w-full text-xs border-collapse mb-4">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border px-2 py-1 text-left">工单号</th>
                      <th className="border px-2 py-1 text-left">产品</th>
                      <th className="border px-2 py-1 text-left">客户</th>
                      <th className="border px-2 py-1 text-left">交期</th>
                      <th className="border px-2 py-1 text-right">计划</th>
                      <th className="border px-2 py-1 text-right">完成</th>
                      <th className="border px-2 py-1 text-right">完成率</th>
                      <th className="border px-2 py-1 text-left">状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.order_number}>
                        <td className="border px-2 py-1">{o.order_number}</td>
                        <td className="border px-2 py-1">{o.product_label}</td>
                        <td className="border px-2 py-1">{o.client_name}</td>
                        <td className="border px-2 py-1">{o.delivery_date}</td>
                        <td className="border px-2 py-1 text-right">{o.planned_quantity}</td>
                        <td className="border px-2 py-1 text-right">{o.completed_quantity}</td>
                        <td className="border px-2 py-1 text-right">{o.progress_percent}%</td>
                        <td className="border px-2 py-1">{o.status_label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-muted-foreground mb-4">本期无活跃工单。</p>
              )}

              {/* 进度风险预警（按工单汇总） */}
              <h3 className="text-sm font-semibold mb-2">
                二、进度风险预警
                <span className="ml-2 font-normal text-xs text-muted-foreground">
                  高 {stats?.risk_high ?? 0} / 中 {stats?.risk_medium ?? 0} / 低 {stats?.risk_low ?? 0}
                </span>
              </h3>
              {riskGroups.length > 0 ? (
                <table className="w-full text-xs border-collapse mb-4">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border px-2 py-1 text-left w-12">级别</th>
                      <th className="border px-2 py-1 text-left w-56">工单 / 产品</th>
                      <th className="border px-2 py-1 text-left">风险摘要</th>
                      <th className="border px-2 py-1 text-right w-12">项数</th>
                    </tr>
                  </thead>
                  <tbody>
                    {riskGroups.map((g, idx) => (
                      <tr key={idx} className={g.level === 'high' ? 'bg-red-50/60' : g.level === 'medium' ? 'bg-amber-50/60' : ''}>
                        <td className="border px-2 py-1 align-top"><RiskBadge level={g.level} /></td>
                        <td className="border px-2 py-1 align-top">{g.order_number} / {g.product}</td>
                        <td className="border px-2 py-1">
                          {(g.items || []).map((item, i) => (
                            <div key={i} className={`flex items-baseline gap-1.5 ${i > 0 ? 'mt-1 pt-1 border-t border-dashed border-gray-200' : ''}`}>
                              <span className={`inline-block px-1.5 py-0 rounded text-[10px] font-semibold whitespace-nowrap shrink-0 ${LEVEL_BADGE_CLASS[item.level] || LEVEL_BADGE_CLASS.low}`}>
                                {item.type_label}
                              </span>
                              <span>{item.short}</span>
                            </div>
                          ))}
                        </td>
                        <td className="border px-2 py-1 text-right align-top">{g.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-muted-foreground mb-4">本期未发现进度风险。</p>
              )}

              {/* 前后段加工依赖分析 */}
              <h3 className="text-sm font-semibold mb-2">三、前后段加工依赖分析</h3>
              {dependencyRows.length > 0 ? (
                <table className="w-full text-xs border-collapse mb-4">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border px-2 py-1 text-left">工单 / 产品</th>
                      <th className="border px-2 py-1 text-left">前段工序(完成/计划)</th>
                      <th className="border px-2 py-1 text-left">后段工序(已排/待排)</th>
                      <th className="border px-2 py-1 text-left">阻塞说明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dependencyRows.map((d, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">{d.order_number} / {d.product}</td>
                        <td className="border px-2 py-1">{d.upstream}</td>
                        <td className="border px-2 py-1">{d.downstream}</td>
                        <td className="border px-2 py-1">{d.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-muted-foreground mb-4">未发现前后段依赖阻塞。</p>
              )}

              {/* AI 状态总结 */}
              <h3 className="text-sm font-semibold mb-2">四、AI 状态总结</h3>
              {data.ai_summary ? (
                <div className="markdown-response text-sm leading-relaxed text-foreground rounded-md border bg-background p-3">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {data.ai_summary}
                  </ReactMarkdown>
                </div>
              ) : (
                <Alert className="mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {data.ai_error ? `AI 总结不可用：${data.ai_error}` : '未生成 AI 总结。'}
                  </AlertDescription>
                </Alert>
              )}
            </ScrollArea>
          )}
        </div>

        <DialogFooter className="px-6 pb-6 pt-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runSummary}
            disabled={loading || exporting}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            重新生成
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('pdf')}
            disabled={loading || exporting || !data}
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <FileText className="h-4 w-4 mr-1" />
            )}
            导出 PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('html')}
            disabled={loading || exporting || !data}
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <FileDown className="h-4 w-4 mr-1" />
            )}
            导出 HTML
          </Button>
          <Button variant="default" size="sm" onClick={onClose} disabled={loading || exporting}>
            关闭
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WorkOrderSummaryDialog;
